/**
 * Cumpli-styled date+time picker: a maroon-brand month calendar plus pill-chip
 * hour/minute selection, replacing the browser-native `datetime-local` control
 * in scheduling flows. No external deps; tokens and idioms match ui.tsx.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from './ui';

const WEEKDAYS = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];
const HOURS = [7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18];
const MINUTES = [0, 15, 30, 45];

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function hourLabel(h: number): string {
  if (h === 0) return '12 a. m.';
  if (h < 12) return `${h} a. m.`;
  if (h === 12) return '12 p. m.';
  return `${h - 12} p. m.`;
}

/** Small pill chip shared by the hour/minute selectors. */
function Chip({
  label,
  selected,
  disabled,
  onClick,
}: {
  label: string;
  selected: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'rounded-full border px-2 py-1.5 text-xs font-semibold transition-colors disabled:pointer-events-none disabled:opacity-35',
        selected
          ? 'border-primary bg-primary text-white shadow-sm'
          : 'border-line bg-canvas text-ink-soft hover:border-primary hover:text-ink'
      )}
    >
      {label}
    </button>
  );
}

/**
 * Controlled picker. `value === null` means nothing chosen yet; picking a day
 * first defaults the time to 9:00 a. m., picking a time first defaults the day
 * to today. `min` bounds the selectable moments (whole past days are disabled;
 * on the min day, past times are disabled).
 */
export function DateTimePicker({
  value,
  min,
  onChange,
}: {
  value: Date | null;
  min?: Date | undefined;
  onChange: (d: Date) => void;
}) {
  const { t } = useTranslation();
  const [viewMonth, setViewMonth] = useState(() => startOfMonth(value ?? min ?? new Date()));

  // Presets in the parent can jump the selection to another month — follow it.
  useEffect(() => {
    if (value) setViewMonth(startOfMonth(value));
  }, [value?.getTime()]);

  const minDay = min ? startOfDay(min) : null;
  const today = startOfDay(new Date());

  const rawMonthLabel = new Intl.DateTimeFormat('es-PA', { month: 'long', year: 'numeric' }).format(viewMonth);
  // Only the first letter — es-PA renders "julio de 2026" and "de" must stay lowercase.
  const monthLabel = rawMonthLabel.charAt(0).toUpperCase() + rawMonthLabel.slice(1);
  const daysInMonth = new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 0).getDate();
  const offset = viewMonth.getDay(); // Sunday-first, matches es-PA convention

  const pickDay = (day: number) => {
    const base = value ?? new Date(viewMonth.getFullYear(), viewMonth.getMonth(), day, 9, 0, 0, 0);
    onChange(
      new Date(viewMonth.getFullYear(), viewMonth.getMonth(), day, base.getHours(), base.getMinutes(), 0, 0)
    );
  };

  const pickTime = (patch: { hours?: number; minutes?: number }) => {
    const next = new Date(value ?? (min ?? new Date()));
    if (patch.hours !== undefined) next.setHours(patch.hours);
    if (patch.minutes !== undefined) next.setMinutes(patch.minutes);
    next.setSeconds(0, 0);
    onChange(next);
  };

  const timeDisabled = (patch: { hours?: number; minutes?: number }): boolean => {
    if (!min) return false;
    const probe = new Date(value ?? min);
    if (patch.hours !== undefined) probe.setHours(patch.hours);
    if (patch.minutes !== undefined) probe.setMinutes(patch.minutes);
    probe.setSeconds(0, 0);
    // Only constrain on the min day itself; any future day is fully open.
    return sameDay(probe, min) && probe.getTime() <= min.getTime();
  };

  return (
    <div className="grid gap-4">
      {/* calendar */}
      <div className="rounded-2xl border border-line bg-panel p-3">
        <div className="mb-2 flex items-center justify-between">
          <button
            type="button"
            aria-label={t('alcance.pickerPrevMonth')}
            onClick={() => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1))}
            className="rounded-lg p-1.5 text-ink-soft transition-colors hover:bg-canvas hover:text-ink"
          >
            <ChevronLeft className="h-4 w-4" strokeWidth={2} />
          </button>
          <div className="font-serif text-sm font-medium text-ink">{monthLabel}</div>
          <button
            type="button"
            aria-label={t('alcance.pickerNextMonth')}
            onClick={() => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1))}
            className="rounded-lg p-1.5 text-ink-soft transition-colors hover:bg-canvas hover:text-ink"
          >
            <ChevronRight className="h-4 w-4" strokeWidth={2} />
          </button>
        </div>
        <div className="grid grid-cols-7 gap-y-0.5 text-center">
          {WEEKDAYS.map((w) => (
            <div key={w} className="py-1 font-mono text-[10px] uppercase tracking-wide text-ink-faint">
              {w}
            </div>
          ))}
          {Array.from({ length: offset }).map((_, i) => (
            <div key={`pad-${i}`} />
          ))}
          {Array.from({ length: daysInMonth }).map((_, i) => {
            const day = i + 1;
            const cellDate = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), day);
            const isPast = minDay !== null && cellDate.getTime() < minDay.getTime();
            const isSelected = value !== null && sameDay(cellDate, value);
            const isToday = sameDay(cellDate, today);
            return (
              <button
                key={day}
                type="button"
                disabled={isPast}
                aria-pressed={isSelected}
                onClick={() => pickDay(day)}
                className={cn(
                  'mx-auto flex h-9 w-9 items-center justify-center rounded-xl text-sm transition-colors disabled:pointer-events-none disabled:text-ink-faint/50',
                  isSelected
                    ? 'bg-primary font-bold text-white shadow-sm'
                    : cn('text-ink hover:bg-canvas', isToday && 'border border-primary/40 font-semibold text-primary')
                )}
              >
                {day}
              </button>
            );
          })}
        </div>
      </div>

      {/* time */}
      <div>
        <div className="mb-1.5 font-mono text-[10px] uppercase tracking-wide text-ink-faint">
          {t('alcance.pickerTimeLabel')}
        </div>
        <div className="grid grid-cols-4 gap-1.5 xs:grid-cols-6">
          {HOURS.map((h) => (
            <Chip
              key={h}
              label={hourLabel(h)}
              selected={value !== null && value.getHours() === h}
              disabled={timeDisabled({ hours: h })}
              onClick={() => pickTime({ hours: h })}
            />
          ))}
        </div>
        <div className="mt-2 grid grid-cols-4 gap-1.5">
          {MINUTES.map((m) => (
            <Chip
              key={m}
              label={`:${String(m).padStart(2, '0')}`}
              selected={value !== null && value.getMinutes() === m}
              disabled={timeDisabled({ minutes: m })}
              onClick={() => pickTime({ minutes: m })}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
